(function (globalScope) {
  const DB_NAME = 'LoggerDB';
  const DB_VERSION = 1;
  const STORE_NAME = 'logs';
  const FAILED_STORE_NAME = 'failedLogs';
  const DEFAULT_MAX_LOGS = 1000;
  const DEFAULT_MAX_FAILED_LOGS = 1500;
  const DEFAULT_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes (1,800,000 ms)
  const GC_INTERVAL_MS = 60 * 1000;
  const INITIAL_BACKOFF_MS = 5000;
  const MAX_BACKOFF_MS = 5 * 60 * 1000;
  const BACKOFF_JITTER_FACTOR = 0.2;
  const RETRYABLE_STATUSES = [408, 429, 500, 502, 503, 504];

  let configuredMaxAgeMs = DEFAULT_MAX_AGE_MS;
  let configuredMaxLogs = DEFAULT_MAX_LOGS;
  let configuredMaxFailedLogs = DEFAULT_MAX_FAILED_LOGS;
  let logsStoreIsFull = false;
  let failedLogsStoreIsFull = false;
  let gcIntervalId = null;
  let gcSweepInFlight = false;

  function normalizeMaxAgeMs(maxAgeMs) {
    if (typeof maxAgeMs !== 'number' || !isFinite(maxAgeMs) || maxAgeMs <= 0) {
      return DEFAULT_MAX_AGE_MS;
    }

    return Math.floor(maxAgeMs);
  }

  function normalizeMaxFailedLogs(maxFailedLogs) {
    if (typeof maxFailedLogs !== 'number' || !isFinite(maxFailedLogs) || maxFailedLogs <= 0) {
      return DEFAULT_MAX_FAILED_LOGS;
    }

    return Math.floor(maxFailedLogs);
  }

  function normalizeMaxLogs(maxLogs) {
    if (typeof maxLogs !== 'number' || !isFinite(maxLogs) || maxLogs <= 0) {
      return DEFAULT_MAX_LOGS;
    }

    return Math.floor(maxLogs);
  }

  function configure(options) {
    if (!options) {
      return;
    }

    if (typeof options.maxAgeMs !== 'undefined') {
      configuredMaxAgeMs = normalizeMaxAgeMs(options.maxAgeMs);
    }

    if (typeof options.maxLogs !== 'undefined') {
      configuredMaxLogs = normalizeMaxLogs(options.maxLogs);
    }

    if (typeof options.maxFailedLogs !== 'undefined') {
      configuredMaxFailedLogs = normalizeMaxFailedLogs(options.maxFailedLogs);
    }

    startGcTimer();
  }

  function runGcSweep() {
    if (gcSweepInFlight) {
      return Promise.resolve();
    }

    gcSweepInFlight = true;
    return Promise.all([expireOldRecords(STORE_NAME), expireOldRecords(FAILED_STORE_NAME)])
      .then(function () {})
      .catch(function () {})
      .then(function () {
        gcSweepInFlight = false;
      });
  }

  function startGcTimer() {
    if (gcIntervalId !== null || typeof globalScope.setInterval !== 'function') {
      return;
    }

    gcIntervalId = globalScope.setInterval(function () {
      runGcSweep();
    }, GC_INTERVAL_MS);

    runGcSweep();
  }

  function ensureStoreIndexes(db, storeName, indexNames) {
    if (!db.objectStoreNames.contains(storeName)) {
      const createdStore = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
      for (let i = 0; i < indexNames.length; i++) {
        createdStore.createIndex(indexNames[i], indexNames[i], { unique: false });
      }
    }
  }

  function openDB() {
    return new Promise(function (resolve, reject) {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = function (event) {
        const db = event.target.result;
        ensureStoreIndexes(db, STORE_NAME, ['routeKey', 'createdAt']);
        ensureStoreIndexes(db, FAILED_STORE_NAME, ['routeKey', 'createdAt']);
      };
      request.onsuccess = function () {
        resolve(request.result);
      };
      request.onerror = function () {
        reject(request.error);
      };
    });
  }

  function emitCapReached(payload) {
    if (!payload || ((payload.logsDropped || 0) <= 0 && (payload.failedLogsDropped || 0) <= 0 && !payload.logsCapReached && !payload.failedLogsCapReached)) {
      return Promise.resolve();
    }

    if (globalScope && globalScope.window && typeof globalScope.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
      globalScope.dispatchEvent(
        new CustomEvent('logger-cap-reached', {
          detail: payload
        })
      );
      return Promise.resolve();
    }

    if (globalScope && globalScope.clients && typeof globalScope.clients.matchAll === 'function') {
      return globalScope.clients
        .matchAll({ includeUncontrolled: true, type: 'window' })
        .then(function (clients) {
          for (const client of clients) {
            client.postMessage({
              type: 'LOGGER_CAP_REACHED',
              logsCapReached: !!payload.logsCapReached,
              failedLogsCapReached: !!payload.failedLogsCapReached,
              logsDropped: payload.logsDropped || 0,
              failedLogsDropped: payload.failedLogsDropped || 0
            });
          }
        })
        .catch(function () {});
    }

    return Promise.resolve();
  }

  function emitStoreAvailable(payload) {
    if (!payload || (!payload.logsStoreAvailable && !payload.failedLogsStoreAvailable)) {
      return Promise.resolve();
    }

    if (globalScope && globalScope.window && typeof globalScope.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
      globalScope.dispatchEvent(
        new CustomEvent('logger-store-available', {
          detail: payload
        })
      );
      return Promise.resolve();
    }

    if (globalScope && globalScope.clients && typeof globalScope.clients.matchAll === 'function') {
      return globalScope.clients
        .matchAll({ includeUncontrolled: true, type: 'window' })
        .then(function (clients) {
          for (const client of clients) {
            client.postMessage({
              type: 'LOGGER_STORE_AVAILABLE',
              logsStoreAvailable: !!payload.logsStoreAvailable,
              failedLogsStoreAvailable: !!payload.failedLogsStoreAvailable
            });
          }
        })
        .catch(function () {});
    }

    return Promise.resolve();
  }

  function storeLogs(logs, routeKey) {
    if (!logs || logs.length === 0) {
      return Promise.resolve();
    }

    const now = Date.now();
    return openDB().then(function (db) {
      return new Promise(function (resolve, reject) {
        let settled = false;
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const countRequest = store.count();

        countRequest.onsuccess = function () {
          const count = countRequest.result || 0;
          const availableSlots = Math.max(configuredMaxLogs - count, 0);
          const acceptedCount = Math.min(logs.length, availableSlots);
          const droppedCount = logs.length - acceptedCount;
          const wasFull = logsStoreIsFull;

          for (let i = 0; i < acceptedCount; i++) {
            store.add({ routeKey: routeKey, createdAt: now, data: logs[i] });
          }

          if (droppedCount > 0) {
            settled = true;
            logsStoreIsFull = true;
            if (!wasFull) {
              emitCapReached({
                logsCapReached: true,
                failedLogsCapReached: false,
                logsDropped: droppedCount,
                failedLogsDropped: 0
              })
                .then(function () {
                  resolve();
                })
                .catch(function () {
                  resolve();
                });
            } else {
              resolve();
            }
            return;
          }

          logsStoreIsFull = count + acceptedCount >= configuredMaxLogs;
          if (!wasFull && logsStoreIsFull) {
            settled = true;
            emitCapReached({
              logsCapReached: true,
              failedLogsCapReached: false,
              logsDropped: 0,
              failedLogsDropped: 0
            })
              .then(function () {
                resolve();
              })
              .catch(function () {
                resolve();
              });
          }
        };

        countRequest.onerror = function () {
          if (!settled) {
            settled = true;
            reject(countRequest.error);
          }
        };

        tx.oncomplete = function () {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        tx.onerror = function () {
          if (!settled) {
            settled = true;
            reject(tx.error);
          }
        };
      });
    });
  }

  function getAllRecordsByRouteKey(storeName, routeKey) {
    return openDB().then(function (db) {
      return new Promise(function (resolve, reject) {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const index = store.index('routeKey');
        const request = index.getAll(routeKey);

        request.onsuccess = function () {
          resolve(request.result || []);
        };
        request.onerror = function () {
          reject(request.error);
        };
      });
    });
  }

  function deleteRecordsByIds(storeName, recordIds) {
    if (!recordIds || recordIds.length === 0) {
      return Promise.resolve();
    }

    return openDB().then(function (db) {
      return new Promise(function (resolveDelete, rejectDelete) {
        const deleteTx = db.transaction(storeName, 'readwrite');
        const deleteStore = deleteTx.objectStore(storeName);
        for (let i = 0; i < recordIds.length; i++) {
          deleteStore.delete(recordIds[i]);
        }
        deleteTx.oncomplete = function () {
          resolveDelete();
        };
        deleteTx.onerror = function () {
          rejectDelete(deleteTx.error);
        };
      });
    });
  }

  function moveLogsToFailed(logRecords, now) {
    if (!logRecords || logRecords.length === 0) {
      return Promise.resolve();
    }

    return openDB().then(function (db) {
      return new Promise(function (resolve, reject) {
        let settled = false;
        const tx = db.transaction(FAILED_STORE_NAME, 'readwrite');
        const failedStore = tx.objectStore(FAILED_STORE_NAME);
        const countRequest = failedStore.count();

        countRequest.onsuccess = function () {
          const count = countRequest.result || 0;
          const availableSlots = Math.max(configuredMaxFailedLogs - count, 0);
          const acceptedCount = Math.min(logRecords.length, availableSlots);
          const droppedCount = logRecords.length - acceptedCount;
          const wasFull = failedLogsStoreIsFull;

          for (let i = 0; i < acceptedCount; i++) {
            const nextRetryAt = now + computeBackoffDelayMs(1);
            failedStore.add({
              routeKey: logRecords[i].routeKey,
              createdAt: logRecords[i].createdAt,
              data: logRecords[i].data,
              attempts: 1,
              lastAttemptAt: now,
              nextRetryAt: nextRetryAt
            });
          }

          if (droppedCount > 0) {
            settled = true;
            failedLogsStoreIsFull = true;
            if (!wasFull) {
              emitCapReached({
                logsCapReached: false,
                failedLogsCapReached: true,
                logsDropped: 0,
                failedLogsDropped: droppedCount
              })
                .then(function () {
                  resolve();
                })
                .catch(function () {
                  resolve();
                });
            } else {
              resolve();
            }
            return;
          }

          failedLogsStoreIsFull = count + acceptedCount >= configuredMaxFailedLogs;
          if (!wasFull && failedLogsStoreIsFull) {
            settled = true;
            emitCapReached({
              logsCapReached: false,
              failedLogsCapReached: true,
              logsDropped: 0,
              failedLogsDropped: 0
            })
              .then(function () {
                resolve();
              })
              .catch(function () {
                resolve();
              });
          }
        };

        countRequest.onerror = function () {
          if (!settled) {
            settled = true;
            reject(countRequest.error);
          }
        };

        tx.oncomplete = function () {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        tx.onerror = function () {
          if (!settled) {
            settled = true;
            reject(tx.error);
          }
        };
      });
    });
  }

  function rescheduleFailedRecords(failedRecords, now) {
    if (!failedRecords || failedRecords.length === 0) {
      return Promise.resolve();
    }

    return openDB().then(function (db) {
      return new Promise(function (resolve, reject) {
        const tx = db.transaction(FAILED_STORE_NAME, 'readwrite');
        const failedStore = tx.objectStore(FAILED_STORE_NAME);
        for (let i = 0; i < failedRecords.length; i++) {
          const record = failedRecords[i];
          const attempts = (record.attempts || 0) + 1;
          record.attempts = attempts;
          record.lastAttemptAt = now;
          record.nextRetryAt = now + computeBackoffDelayMs(attempts);
          failedStore.put(record);
        }

        tx.oncomplete = function () {
          resolve();
        };
        tx.onerror = function () {
          reject(tx.error);
        };
      });
    });
  }

  function expireOldRecords(storeName) {
    const cutoff = Date.now() - configuredMaxAgeMs;
    return openDB().then(function (db) {
      return new Promise(function (resolve, reject) {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const index = store.index('createdAt');
        let expired = 0;
        const cursorRequest = index.openCursor();

        cursorRequest.onsuccess = function (event) {
          const cursor = event.target.result;
          if (cursor) {
            if (cursor.value.createdAt < cutoff) {
              expired++;
              cursor.delete();
            }

            cursor.continue();
          } else {
            resolve({ expired: expired });
          }
        };

        cursorRequest.onerror = function () {
          reject(cursorRequest.error);
        };
      });
    });
  }

  function isRetryableStatus(status) {
    return RETRYABLE_STATUSES.indexOf(status) !== -1;
  }

  function computeBackoffDelayMs(attempts) {
    const normalizedAttempts = attempts > 0 ? attempts : 1;
    const exponentialDelay = INITIAL_BACKOFF_MS * Math.pow(2, normalizedAttempts - 1);
    const cappedDelay = Math.min(exponentialDelay, MAX_BACKOFF_MS);
    const jitter = Math.floor(cappedDelay * BACKOFF_JITTER_FACTOR * Math.random());
    return cappedDelay + jitter;
  }

  function flushFailedLogsCore(routeKey, endpoint) {
    return getAllRecordsByRouteKey(FAILED_STORE_NAME, routeKey).then(function (failedRecords) {
      const now = Date.now();
      const dueRecords = [];
      for (let i = 0; i < failedRecords.length; i++) {
        const record = failedRecords[i];
        if (!record.nextRetryAt || record.nextRetryAt <= now) {
          dueRecords.push(record);
        }
      }

      if (dueRecords.length === 0) {
        return { ok: true, sentCount: 0 };
      }

      const logData = dueRecords.map(function (r) {
        return r.data;
      });
      const recordIds = dueRecords.map(function (r) {
        return r.id;
      });

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(logData)
      })
        .then(function (response) {
          if (response.ok) {
            return deleteRecordsByIds(FAILED_STORE_NAME, recordIds).then(function () {
              return { ok: true, sentCount: logData.length };
            });
          }

          if (isRetryableStatus(response.status)) {
            return rescheduleFailedRecords(dueRecords, now).then(function () {
              return {
                ok: false,
                sentCount: 0,
                retryable: true,
                errorMessage: 'Server returned ' + response.status
              };
            });
          }

          return deleteRecordsByIds(FAILED_STORE_NAME, recordIds).then(function () {
            return {
              ok: false,
              sentCount: 0,
              retryable: false,
              errorMessage: 'Non-retryable status ' + response.status
            };
          });
        })
        .catch(function (error) {
          return rescheduleFailedRecords(dueRecords, now).then(function () {
            return {
              ok: false,
              sentCount: 0,
              retryable: true,
              errorMessage: error && error.message ? error.message : 'Network failure'
            };
          });
        });
    });
  }

  function flushNewLogsCore(routeKey, endpoint) {
    return getAllRecordsByRouteKey(STORE_NAME, routeKey).then(function (records) {
      if (!records || records.length === 0) {
        return { ok: true, sentCount: 0 };
      }

      const now = Date.now();
      const logData = records.map(function (r) {
        return r.data;
      });
      const recordIds = records.map(function (r) {
        return r.id;
      });

      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(logData)
      })
        .then(function (response) {
          if (response.ok) {
            return deleteRecordsByIds(STORE_NAME, recordIds).then(function () {
              return { ok: true, sentCount: logData.length };
            });
          }

          if (isRetryableStatus(response.status)) {
            return moveLogsToFailed(records, now)
              .then(function () {
                return deleteRecordsByIds(STORE_NAME, recordIds);
              })
              .then(function () {
                return {
                  ok: false,
                  sentCount: 0,
                  retryable: true,
                  errorMessage: 'Server returned ' + response.status
                };
              });
          }

          return deleteRecordsByIds(STORE_NAME, recordIds).then(function () {
            return {
              ok: false,
              sentCount: 0,
              retryable: false,
              errorMessage: 'Non-retryable status ' + response.status
            };
          });
        })
        .catch(function (error) {
          return moveLogsToFailed(records, now)
            .then(function () {
              return deleteRecordsByIds(STORE_NAME, recordIds);
            })
            .then(function () {
              return {
                ok: false,
                sentCount: 0,
                retryable: true,
                errorMessage: error && error.message ? error.message : 'Network failure'
              };
            });
        });
    });
  }

  function flushLogsCore(routeKey, endpoint) {
    return flushFailedLogsCore(routeKey, endpoint)
      .then(function (failedResult) {
        return flushNewLogsCore(routeKey, endpoint).then(function (newResult) {
          return {
            failedResult: failedResult,
            newResult: newResult
          };
        });
      })
      .then(function (results) {
        const failedResult = results.failedResult;
        const newResult = results.newResult;

        const sentCount = (failedResult.sentCount || 0) + (newResult.sentCount || 0);
        const ok = failedResult.ok && newResult.ok;
        const errorMessage = failedResult.errorMessage || newResult.errorMessage;
        const retryable = failedResult.retryable || newResult.retryable;

        if (newResult.sentCount > 0 || newResult.retryable || !newResult.ok) {
          logsStoreIsFull = false;
        }

        if (failedResult.sentCount > 0 || (!failedResult.ok && !failedResult.retryable)) {
          failedLogsStoreIsFull = false;
        }
        const logsStoreAvailable = !logsStoreIsFull;
        const failedLogsStoreAvailable = !failedLogsStoreIsFull;

        return emitStoreAvailable({
          logsStoreAvailable: logsStoreAvailable,
          failedLogsStoreAvailable: failedLogsStoreAvailable
        }).then(function () {
          return {
            ok: ok,
            sentCount: sentCount,
            retryable: !!retryable,
            errorMessage: errorMessage
          };
        });
      });
  }

  function flushLogs(routeKey, endpoint) {
    return flushLogsCore(routeKey, endpoint).catch(function () {
      // Preserve fire-and-forget behavior for background flushes
    });
  }

  function getConfig() {
    return {
      maxLogs: configuredMaxLogs,
      maxFailedLogs: configuredMaxFailedLogs,
      maxAgeMs: configuredMaxAgeMs
    };
  }

  globalScope.LoggerDbCore = {
    configure: configure,
    storeLogs: storeLogs,
    flushLogsCore: flushLogsCore,
    flushLogs: flushLogs,
    getConfig: getConfig
  };
})(self);
